/**
 * The "Vault" theme — React Native Paper MD3, hand-tuned.
 *
 * Hand-ported from `mobile/theme/paper-theme.ts`, which stays canonical. There
 * is no workspace and no shared package between the iOS app and this site, so
 * this is a copy rather than an import, and the two files are kept in sync by
 * hand: when a colour moves in the mobile file, mirror it here (research D9).
 * Never edit `mobile/theme/` to suit the web — the iOS app's workflows must
 * stay byte-for-byte unaffected (FR-008). Where the web has to differ, the
 * change is made here and noted below.
 *
 * Sparkie turns speech into durable, linked markdown, so the palette stays
 * mostly neutral and exactly one hue is alive: `tertiary` means something is
 * happening right now — recording, playing, reading aloud. `primary` (slate
 * teal) carries actions and links, `error` carries failures, and
 * `status.success` marks completion.
 *
 * Built from five seeds. Golden Earth and Royal Gold are the same accent at
 * two lightnesses, so the "live" hue swaps between them by scheme rather than
 * needing a separate tone:
 *
 *   #2A4747  Dark Slate Grey  light primary        / dark inversePrimary
 *   #99621E  Golden Earth     light tertiary*      / dark tertiaryContainer
 *                             (*darkened to #8A581B for contrast — see below)
 *   #F4D35E  Royal Gold       light tertiaryContainer / dark tertiary
 *   #A2AEBB  Cool Steel       light outline family / dark secondary + onSurfaceVariant
 *   #3B3B58  Space Indigo     light secondary      / dark secondaryContainer
 *
 * "Live" sits 38-44 degrees of hue from "failed" so the two statuses stay
 * distinguishable — they are additionally never signalled by colour alone.
 *
 * Deliberate differences from the mobile file:
 *
 * 1. No React Navigation. `adaptNavigationTheme` and the two navigation theme
 *    exports are dropped — the site routes with TanStack Router, which takes
 *    no theme object.
 * 2. Fonts are CSS family stacks at real weights (see the typescale below).
 * 3. No `transcriptTextStyle` / `dataTextStyle`. The site renders no
 *    long-form transcript and no mono data, and shipping a font family the
 *    page never renders is exactly what FR-021(b) forbids.
 * 4. The colour values live in `vault-palette.ts` so `contrast.test.ts` can
 *    audit them without loading Paper under jsdom.
 * 5. Two light-scheme values are adjusted for contrast — see below.
 *
 * WCAG 2.2 Level AA audit (FR-019(c)). Every pair in both schemes was measured
 * against 4.5:1 for text and 3:1 for the boundaries that identify a control;
 * `contrast.test.ts` re-measures every audited pair on each run. The Vault
 * palette was tuned for iOS, and two light-scheme values did not clear the bar
 * on a raised web surface, where Paper tints cards and menus from the
 * elevation ramp rather than painting them `surface`. FR-019 makes the
 * contrast minimum win over palette fidelity, so both moved by the smallest
 * change that clears it — HSL lightness only, hue and saturation untouched, so
 * the identity is unchanged to the eye:
 *
 *   light `tertiary`        #99621E → #8A581B  (L 35.9% → 32.4%)
 *     on elevation level1-5   4.46 / 4.25 / 4.02 / 3.98 / 3.83  ✗
 *                          →  5.27 / 5.01 / 4.75 / 4.69 / 4.52  ✓
 *     on surface              4.84 → 5.71 ✓;  #FFFFFF on it 5.10 → 6.01 ✓
 *
 *   light `status.success`  #37743F → #356F3C  (L 33.5% → 32.1%)
 *     on elevation level3-5   4.44 / 4.39 / 4.23  ✗
 *                          →  4.75 / 4.69 / 4.52  ✓
 *     on surface              5.34 → 5.71 ✓
 *
 * Nothing else moved: every other pair already cleared AA in both schemes, and
 * the dark scheme passed unchanged. The two adjustments are web-only —
 * `mobile/theme/paper-theme.ts` keeps the original values, which are correct
 * for the app's own surfaces and which FR-008 requires stay untouched (Golden
 * Earth is still #99621E there, and in this file's dark `tertiaryContainer`).
 *
 * Container fills, the `outlineVariant` divider and the elevation tint carry
 * no contrast requirement here and are deliberately left alone: every control
 * on this site is identified by its outline and its text label rather than by
 * its fill, and inactive controls are exempt from 1.4.3. `contrast.test.ts`
 * states both exclusions. T052 re-checks the rendered pages in a browser.
 */

import {
  configureFonts,
  MD3DarkTheme,
  MD3LightTheme,
  useTheme,
} from "react-native-paper";
import type { MD3Theme } from "react-native-paper";
import type { StatusColors } from "./vault-palette";
import {
  vaultDarkColors,
  vaultDarkStatus,
  vaultLightColors,
  vaultLightStatus,
} from "./vault-palette";

export const SERIF_STACK = "'Source Serif 4', Georgia, serif";
export const SANS_STACK = "'IBM Plex Sans', system-ui, sans-serif";

/**
 * Divergence 6 (recorded): the mobile theme renders data text in
 * IBMPlexMono_400Regular; the web deliberately renders its few chrome-scale
 * mono strings (the phone mock's timers, the how-it-works ordinals) in the
 * system mono stack so no third font family is requested (FR-021(b)) —
 * `dataTextStyle` itself remains un-ported. The layout's font URL omits IBM
 * Plex Mono on exactly this basis.
 */
export const MONO_STACK =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

export type AppTheme = MD3Theme & { status: StatusColors };

/**
 * Typescale: the big stuff is serif (it reads as a document), everything else
 * is sans — the same split as the app.
 *
 * Unlike the mobile file, each variant asks for a real weight rather than
 * naming a pre-weighted font file and pinning `fontWeight: 'normal'`. That
 * pinning is an iOS guard against smeared synthetic bold on an already-bold
 * file; the browser loads genuine 400/500/600 faces, so carrying the guard
 * across would flatten the typography instead of protecting it (research D9).
 */
const fonts = configureFonts({
  config: {
    displayLarge: { fontFamily: SERIF_STACK, fontWeight: "600" },
    displayMedium: { fontFamily: SERIF_STACK, fontWeight: "600" },
    displaySmall: { fontFamily: SERIF_STACK, fontWeight: "600" },

    headlineLarge: { fontFamily: SERIF_STACK, fontWeight: "600" },
    headlineMedium: { fontFamily: SERIF_STACK, fontWeight: "600" },
    headlineSmall: { fontFamily: SERIF_STACK, fontWeight: "600" },

    titleLarge: { fontFamily: SERIF_STACK, fontWeight: "600" },
    titleMedium: { fontFamily: SANS_STACK, fontWeight: "500" },
    titleSmall: { fontFamily: SANS_STACK, fontWeight: "500" },

    labelLarge: { fontFamily: SANS_STACK, fontWeight: "500" },
    labelMedium: { fontFamily: SANS_STACK, fontWeight: "500" },
    labelSmall: { fontFamily: SANS_STACK, fontWeight: "500" },

    bodyLarge: { fontFamily: SANS_STACK, fontWeight: "400" },
    bodyMedium: { fontFamily: SANS_STACK, fontWeight: "400" },
    bodySmall: { fontFamily: SANS_STACK, fontWeight: "400" },
  },
});

export const vaultLight: AppTheme = {
  ...MD3LightTheme,
  fonts,
  colors: { ...MD3LightTheme.colors, ...vaultLightColors },
  status: vaultLightStatus,
};

export const vaultDark: AppTheme = {
  ...MD3DarkTheme,
  fonts,
  colors: { ...MD3DarkTheme.colors, ...vaultDarkColors },
  status: vaultDarkStatus,
};

/** Theme hook typed with the `status` extension. */
export const useAppTheme = () => useTheme<AppTheme>();
